"use client"

import type React from "react"

import { useState } from "react"
import Image from "next/image"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Heart, ZoomIn } from "lucide-react"
import type { UIGalleryItem } from "./GalleryPage"
import { useDisplayRoomType, useDisplayRoomTheme } from "@/hooks/useRoomMappings"

interface GalleryCardProps {
  item: UIGalleryItem
  onLikeToggle: (itemId: string) => void
  onImageClick: (item: UIGalleryItem) => void
  showLoginTooltipForImage: string | null // Changed from boolean to track specific image ID
}

export function GalleryCard({ item, onLikeToggle, onImageClick, showLoginTooltipForImage }: GalleryCardProps) {
  const [isHovered, setIsHovered] = useState(false)

  const displayRoomType = useDisplayRoomType(item.roomType)
  const displayRoomTheme = useDisplayRoomTheme(item.roomTheme)

  const transformTitle = (title: string, originalRoomType: string, displayRoomType: string) => {
    // Replace the original room type in the title with the display version
    return title.replace(originalRoomType, displayRoomType)
  }

  const displayTitle = transformTitle(item.title, item.roomType, displayRoomType)

  const handleLike = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    console.log("[v0] Heart clicked for item:", item.id, "Current liked state:", item.isLiked)
    onLikeToggle(item.id)
  }

  const handleImageClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onImageClick(item)
  }

  return (
    <Card
      className="overflow-hidden transition-all duration-300 hover:shadow-lg hover:scale-[1.02]"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardContent className="p-0">
        <div className="relative aspect-[4/3] overflow-hidden group cursor-pointer" onClick={handleImageClick}>
          <Image
            src={item.imageUrl || "/placeholder.svg"}
            alt={item.title}
            fill
            className="object-cover transition-transform duration-200 group-hover:scale-105"
          />

          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
            <div className="bg-white/90 rounded-full p-3">
              <ZoomIn className="h-6 w-6 text-gray-700" />
            </div>
          </div>

          <div className="absolute top-3 right-3">
            <Badge variant="secondary" className="bg-white/90 text-gray-700">
              {displayRoomTheme}
            </Badge>
          </div>

          <div className="absolute top-3 left-3">
            <div className="relative">
              <Button
                size="sm"
                variant="secondary"
                className="bg-white/90 hover:bg-white text-gray-700 p-2 z-10"
                onClick={handleLike}
              >
                <Heart className={`h-4 w-4 ${item.isLiked ? "fill-red-500 text-red-500" : ""}`} />
              </Button>
              {showLoginTooltipForImage === item.id && ( // Only show tooltip if this image's ID matches
                <div className="absolute top-full left-0 mt-2 w-48 bg-gray-900 text-white text-xs rounded-lg p-2 shadow-lg z-20 animate-in fade-in slide-in-from-top-2 duration-200">
                  Please log in to add your like to this image.
                  <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 rotate-45"></div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-4">
          <h3 className="font-semibold text-lg mb-2">{displayTitle}</h3>
          <div className="flex items-center justify-between text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <Heart className={`h-4 w-4 ${item.isLiked ? "fill-red-500 text-red-500" : ""}`} />
              <span>{item.likeCount}</span>
            </div>
            <span className="capitalize">
              {displayRoomType} • {displayRoomTheme}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
